import { getAuth, signOut } from "firebase/auth";
import {
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  Unsubscribe,
} from "firebase/firestore";
import { getActivePinia } from "pinia";
import { combineLatest, Subscription } from "rxjs";
import { AppUser } from "types/AppUser";
import { Article } from "types/Article";
import { CustomClaims } from "types/CustomClaims";
import { PermissionsService } from "~/classes/models/auth/permissions.service";
import { GoogleOauthToken } from "~/classes/models/google-oauth/google-oauth-token.model";
import { GoogleOauthService } from "~/classes/models/google-oauth/google-oauth.service";
import { LMSOrganizationConfig } from "~/classes/models/organizations/lms-organization-config.model";
import { OrganizationsService } from "~/classes/models/organizations/organizations.service";
import { ZoomOauthToken } from "~/classes/models/zoom-oauth/zoom-oauth-token.model";
import { ZoomOauthService } from "~/classes/models/zoom-oauth/zoom-oauth.service";
import { GoogleAPIScope } from "~/types/enums/GoogleAPIScopes.enum";
import { LMSUserConfig } from "~/types/lmsUserConfig";

export const useAppUserData = (userId: string) =>
  defineStore(`/app/user/data/${userId}`, () => {
    const isInitialized = ref(false);
    const hasInitializedUserLMSConfig = ref(false);
    const isLoggedIn = ref(false);
    const appUser = ref<AppUser>();

    const customClaims = ref<CustomClaims>({
      ...useDefaultUserClaims(),
    });
    const actorClaims = ref<CustomClaims>({
      ...useDefaultUserClaims(),
    });

    const dbClaims = ref<DBCustomClaims>({
      ...useDefaultUserClaims(),
    });

    const zoomOauthToken = ref<ZoomOauthToken | undefined>();
    const googleOauthToken = ref<GoogleOauthToken | undefined>();
    const lmsUserConfig = ref<LMSUserConfig>();
    const lmsOrganizationConfig = ref<LMSOrganizationConfig[]>([]);
    const zoomSubscription = ref<Subscription | null>(null);
    const googleSubscription = ref<Subscription | null>(null);
    const appUserSubscription = ref<Unsubscribe | null>(null);
    const lmsUserConfigSubscription = ref<Unsubscribe | null>(null);
    const lmsOrganizationConfigSubscription = ref<Subscription | null>(null);

    const { initializeUserQuickComments } = useUserQuickComments();

    const { initializeAnnotations } = useUserAnnotations();

    const logout = async () => {
      // unsubscribe from all subscriptions
      zoomSubscription.value?.unsubscribe();
      googleSubscription.value?.unsubscribe();
      appUserSubscription.value?.();
      lmsUserConfigSubscription.value?.();
      lmsOrganizationConfigSubscription.value?.unsubscribe();

      // If a store has a reset function implemented, we call it. This ensures the store is being
      const activePinia = getActivePinia();

      ((activePinia as any)?._s as any[]).forEach((store: any) => {
        if (store.reset) {
          store.reset();
        }
      });

      isInitialized.value = false;
      appUser.value = undefined;
      lmsUserConfig.value = undefined;
      lmsOrganizationConfig.value = [];
      zoomOauthToken.value = undefined;
      googleOauthToken.value = undefined;

      useUserQuickComments().reset();
      useUserAnnotations().reset();

      const app = useFirebaseApp();
      const auth = getAuth(app);

      if (!auth) return;

      isLoggedIn.value = false;
      await signOut(auth);
    };

    const initializeAppUserData = async () => {
      if (isInitialized.value == true) {
        return;
      }

      const uid = useCurrentUID();

      if (!uid) {
        return null;
      }

      isLoggedIn.value = true;

      const db = useFirestore();

      try {
        const lmsUserConfigRef = doc(db, `/lmsUserConfigs/${uid}`);

        if (lmsUserConfigSubscription.value) {
          lmsUserConfigSubscription.value();
        }

        lmsUserConfigSubscription.value = onSnapshot(
          lmsUserConfigRef,
          (snapshot) => {
            hasInitializedUserLMSConfig.value = true;
            lmsUserConfig.value = snapshot.data() as LMSUserConfig;
          },
          (error) => {
            // Empty
          }
        );
      } catch (error) {
        // empty
      }

      try {
        const appUserDocRef = doc(db, `/users/${uid}`);
        const appUserDocSnapshot = await getDoc(appUserDocRef);

        // If the app user doc exists and has the email feild hten we can be confidence that the app user doc was created properly
        // This helps protect from partially created app user docs
        if (appUserDocSnapshot.exists() && appUserDocSnapshot.data().Email) {
          const data = appUserDocSnapshot.data() as AppUser;

          appUser.value = {
            ...data,
            criteriaTagsToIgnore: data.criteriaTagsToIgnore ?? [],
          };
        } else {
          const createdDoc = await createAppUser();

          if (createdDoc) {
            appUser.value = createdDoc;
          }
        }

        if (appUserSubscription.value) {
          appUserSubscription.value();
        }

        appUserSubscription.value = onSnapshot(appUserDocRef, (snapshot) => {
          const data = snapshot.data() as AppUser;

          appUser.value = {
            ...data,
            criteriaTagsToIgnore: data.criteriaTagsToIgnore ?? [],
          };
        });

        zoomSubscription.value?.unsubscribe();

        zoomSubscription.value = ZoomOauthService.streamRecord(uid).subscribe(
          (token) => {
            zoomOauthToken.value = token;
          }
        );

        googleSubscription.value?.unsubscribe();

        googleSubscription.value = GoogleOauthService.streamRecord(
          uid
        ).subscribe((token) => {
          googleOauthToken.value = token;
        });
      } catch (error) {
        // empty
      }

      isInitialized.value = true;

      try {
        await initializeUserQuickComments();
      } catch (error) {
        // empty
      }

      try {
        await initializeAnnotations();
      } catch (error) {
        // empty
      }
    };

    const isUserClaimsInitialized = ref(false);
    const initializeUserClaims = async () => {
      if (isUserClaimsInitialized.value == true) {
        return;
      }

      await loadUserPermissions();

      isUserClaimsInitialized.value = true;
    };

    const loadUserPermissions = async () => {
      loadDBClaims();

      const user = useCurrentUser();

      const idTokenResult = await user.value?.getIdTokenResult(true);
      actorClaims.value = {
        ...useDefaultUserClaims(),
        ...(idTokenResult?.claims ?? {}),
      } as CustomClaims;

      let claims: any = idTokenResult?.claims;

      if (usePseudoLogin().isPseudoLogin == true) {
        claims = await $fetch(
          `/api/admin/auth/${usePseudoLogin().pseudoId}/claims`,
          {
            method: "GET",
            headers: await useApiHeaders(),
          }
        );
      }

      if (claims) {
        customClaims.value = {
          isAdmin: claims.isAdmin ?? false,
          isProductReviewer: claims.isProductReviewer,
          isStudentAccount: claims.isStudentAccount,
          isSystemAdmin: claims.isSystemAdmin,
          isDemoAccount: claims.isDemoAccount,
          isPublicDemo: claims.isPublicDemo ?? false,
          isIEWInstructor: claims.isIEWInstructor ?? false,
          availableIEWProductCategoryIds:
            claims.availableIEWProductCategoryIds ?? [],
          availableLevelIds: claims.availableLevelIds ?? [],
          availableLessonIds: claims.availableLessonIds ?? [],
          classroomPermissions: claims.classroomPermissions ?? [],
          studentPermissions: claims.studentPermissions ?? [],
          studentAccountTypes: claims.studentAccountTypes ?? [],
          isAdvancedClassroomSettingsEnabled:
            claims.isAdvancedClassroomSettingsEnabled ?? false,
          linkedOrganizationIds: claims.linkedOrganizationIds ?? [],
          adminOrganizationIds: claims.adminOrganizationIds ?? [],
          permissions: claims.permissions ?? [],
        } as CustomClaims;

        const missingClaims: { [key: string]: any } = {};

        for (const key in useDefaultUserClaims()) {
          if (claims[key] != undefined) continue;

          missingClaims[key] = (useDefaultUserClaims() as any)[key];
        }

        if (Object.keys(missingClaims).length > 0) {
          // Then here wi'll send the missing claims to the server to be updated
          await $fetch(`/api/auth/account/missing-claims`, {
            method: "POST",
            headers: await useApiHeaders(),
          });
        }
      }
    };

    const isDBClaimsInitialized = ref(false);

    const loadDBClaims = () => {
      const uid = useCurrentUID();

      if (!uid) {
        return;
      }

      PermissionsService.streamUserCustomClaims({ userId: uid }).subscribe(
        (claims) => {
          dbClaims.value = claims;
          isDBClaimsInitialized.value = true;
        }
      );
    };

    const createAppUser = async () => {
      const user = useCurrentUser();

      const uid = user.value?.uid;

      if (!uid) {
        return null;
      }

      const appUserDoc = {
        id: uid,
        allowNotifications: false,
        allowEmails: false,
        displayName: user.value?.displayName,
        Email: user.value?.email,
        createdOnTimestamp: Date.now(),
        enableBackgroundSync: false,
        showInlineAnnotations: true,
        hasInvertedScrolling: false,
        lastViewedTipDate: useCurrentDateString(),
        hideTutorials: false,
        enableClassrooms: false,
        dashboardLevels: [],
        numGradedDocuments: 0,
        criteriaTagsToIgnore: [],
        ...(
          useSubdomain().value === "companion" ||
          useRoute().path.startsWith("/companion")
            ? { portal: "companion" as const }
            : {}
        ),
      } as AppUser;

      const db = useFirestore();

      const appUserDocRef = doc(db, `/users/${uid}`);
      await setDoc(appUserDocRef, appUserDoc, { merge: true });

      // Save default annotations
      const defaultAnnotations = useUserAnnotations().defaultAnnotations;

      let index = 0;

      for (const annotation of defaultAnnotations) {
        annotation.displayOrder = index;
        annotation.isEnabled = true;
        annotation.isQuickAnnotation = true;
        index++;
      }

      await useUserAnnotations().saveUserAnnotations(defaultAnnotations);

      return appUserDoc;
    };

    const commentColor = computed(() => {
      return appUser.value?.commentColor ?? [255, 0, 0];
    });

    const saveUser = async () => {
      // We don't want to use the pseudo login here and risk overwrriting a user's doc data.
      const uid = useCurrentUID(false);
      const pseudoLoginStore = usePseudoLogin();

      if (pseudoLoginStore.isPseudoLogin == true) {
        return;
      }

      if (appUser.value?.id != undefined && appUser.value?.id != uid) {
        return;
      }

      if (!uid) {
        return null;
      }

      const db = useFirestore();

      const appUserDocRef = doc(db, `/users/${uid}`);
      await setDoc(appUserDocRef, appUser.value, { merge: true });
    };

    const hasLMSConfig = computed(() => {
      return lmsUserConfig.value != undefined;
    });

    const hasIEWLMS = computed(() => {
      return (organizationId?: string) => {
        if (organizationId != undefined) {
          const org = organiationLMSConfig.value(organizationId);
          if (org != undefined && org?.provider == "iew") {
            return true;
          }
        }

        if (
          (isDemoAccount.value || isPublicDemo.value) &&
          demoMode.value != "iew"
        ) {
          return false;
        }

        return lmsUserConfig.value?.provider == "iew";
      };
    });

    const hasSakaiLMS = computed(() => {
      return (organizationId?: string) => {
        if (organizationId != undefined) {
          const org = organiationLMSConfig.value(organizationId);
          if (org != undefined && org?.provider == "sakai") {
            return true;
          }
        }

        return lmsUserConfig.value?.provider == "sakai";
      };
    });

    const hasClassroomMode = computed(() => {
      return appUser?.value?.enableClassrooms == true;
    });

    const isUserAdmin = computed(() => {
      return (
        actorClaims.value?.isAdmin ||
        customClaims.value?.isAdmin ||
        isUserSystemAdmin.value
      );
    });

    const isUserSystemAdmin = computed(() => {
      return actorClaims.value?.isSystemAdmin === true;
    });

    const isIEWInstructor = computed(() => {
      return dbClaims.value?.isIEWInstructor;
    });

    const isUserProductReviewer = computed(() => {
      return isUserAdmin.value || customClaims.value?.isProductReviewer;
    });

    const isDemoAccount = computed((): boolean => {
      return customClaims.value?.isDemoAccount ?? false;
    });

    const isPublicDemo = computed((): boolean => {
      return customClaims.value?.isPublicDemo ?? false;
    });
    const demoMode = ref("iew");

    const availableIEWProductCategoryIds = computed(() => {
      return dbClaims.value?.availableIEWProductCategoryIds ?? [];
    });

    const availableLevelIds = computed(() => {
      return dbClaims.value?.availableLevelIds ?? [];
    });

    const availableLessonIds = computed(() => {
      return dbClaims.value?.availableLessonIds ?? [];
    });

    const classroomPermissions = computed(() => {
      return customClaims.value?.classroomPermissions ?? [];
    });

    const studentPermissions = computed(() => {
      return customClaims.value?.studentPermissions ?? [];
    });

    const studentAccountTypes = computed(() => {
      return customClaims.value?.studentAccountTypes ?? {};
    });

    const studentAccountType = computed(() => {
      return (studentId: string) => {
        return studentAccountTypes.value[studentId];
      };
    });

    const isStudentAccount = computed(() => {
      return customClaims.value?.isStudentAccount ?? false;
    });

    const hasStudentAccount = computed(() => {
      return (
        isStudentAccount.value &&
        customClaims.value?.classroomPermissions.length > 0
      );
    });

    const lastViewedTipIndex = computed((): number => {
      return appUser.value?.lastViewedTipIndex ?? 0;
    });

    const lastViewedTipDate = computed((): string | undefined => {
      return appUser.value?.lastViewedTipDate;
    });

    const hideTips = computed((): boolean => {
      return appUser.value?.hideTips ?? false;
    });

    const updateUserLastViewedTip = async (article: Article) => {
      if (!appUser.value) return;
      appUser.value!.lastViewedTipDate = useCurrentDateString();
      appUser.value.lastViewedTipIndex = article.tipDisplayOrder;
      saveUser();
    };

    const completedTours = computed(() => {
      return appUser.value?.completedTours ?? [];
    });

    const markTourAsComplete = async (tourId: string) => {
      if (!appUser.value) return;
      if (completedTours.value.includes(tourId)) return;

      appUser.value.completedTours = [...completedTours.value, tourId];
      await saveUser();
    };

    const isTourComplete = computed(() => {
      return (tourId: string) => {
        return completedTours.value.includes(tourId);
      };
    });

    const hideTutorials = async () => {
      if (!appUser.value) return;
      appUser.value.hideTutorials = true;
      await saveUser();
    };

    const shoudHideTutorial = computed(() => {
      return appUser.value?.hideTutorials ?? false;
    });

    const numGradedDocuments = computed(() => {
      return appUser.value?.numGradedDocuments ?? 0;
    });

    const hasLinkedGoogleAccount = computed(() => {
      return appUser.value?.hasLinkedGoogleAccount ?? false;
    });

    const shouldHideSubmissionNotice = computed(() => {
      return appUser.value?.hideSubmissionNotice ?? false;
    });

    const hideSubmissionNotice = async () => {
      if (!appUser.value) return;
      appUser.value.hideSubmissionNotice = true;
      await saveUser();
    };

    // Zoom
    const hasZoomOauthToken = computed(() => {
      return zoomOauthToken.value != undefined;
    });

    // Google OAtuh
    const hasGoogleOauthToken = computed(() => {
      return googleOauthToken.value != undefined;
    });

    const hasGoogleScope = computed(() => {
      return (scope: GoogleAPIScope) => {
        return googleOauthToken.value?.hasScope(scope) ?? false;
      };
    });
    const isAdvancedClassroomSettingsEnabled = computed(() => {
      return customClaims.value?.isAdvancedClassroomSettingsEnabled ?? false;
    });

    const adminOrganizationIds = computed(() => {
      return customClaims.value?.adminOrganizationIds ?? [];
    });

    const linkedOrganizationIds = computed(() => {
      return customClaims.value?.linkedOrganizationIds ?? [];
    });

    const isOrganizationAdministrator = computed(() => {
      return adminOrganizationIds.value.length > 0;
    });

    const isAdministatorForMultipleOrganizations = computed(() => {
      return adminOrganizationIds.value.length > 1;
    });

    const isAdminOfOrganization = computed(() => {
      return (organizationId: string) => {
        return (
          isUserSystemAdmin.value === true ||
          hasPermission.value("write-organizations", { allowAdmin: true }) ||
          actorClaims.value.adminOrganizationIds?.includes(organizationId) ||
          adminOrganizationIds.value.includes(organizationId)
        );
      };
    });

    const isPartOfAnyOrganization = computed(() => {
      return (customClaims.value?.linkedOrganizationIds?.length ?? 0) > 0;
    });

    const isOnlineInstructor = computed(() => {
      return customClaims.value?.isIEWInstructor ?? false;
    });

    const createdOnTimestamp = computed(() => {
      return appUser.value?.createdOnTimestamp;
    });

    // Permissions
    const hasPermission = computed(() => {
      return (
        permissionKey: string,
        options?: { allowSysAdmin?: boolean; allowAdmin?: boolean }
      ) => {
        if (options?.allowAdmin == true && isUserAdmin.value == true) {
          return true;
        }

        if (options?.allowSysAdmin == true && isUserSystemAdmin.value == true) {
          return true;
        }

        return (
          actorClaims.value.permissions?.includes(permissionKey) ||
          customClaims.value.permissions?.includes(permissionKey)
        );
      };
    });

    const canWriteUserRoles = computed(() => {
      return hasPermission.value("write-user-roles") || isUserSystemAdmin.value;
    });

    const canWriteUserPermissions = computed(() => {
      return (
        hasPermission.value("write-user-permissions") || isUserSystemAdmin.value
      );
    });

    const showOtherUsersClassrooms = computed(() => {
      return appUser.value?.showOtherUsersClassrooms ?? true;
    });

    const showOtherUsersSubmissions = computed(() => {
      return appUser.value?.showOtherUsersSubmissions ?? true;
    });

    const updateHomePageHorizontalLayout = async (horizontal: number) => {
      if (!appUser.value) return;

      if (appUser.value.homePageLayout == undefined) {
        appUser.value.homePageLayout = {
          horizontal: 0,
          vertical: 0,
        };
      }

      appUser.value.homePageLayout.horizontal = horizontal;
      await saveUser();
    };

    const updateHomePageVerticalLayout = async (vertical: number) => {
      if (!appUser.value) return;

      if (appUser.value.hideAssignmentProgress == true) {
        return;
      }

      if (appUser.value.homePageLayout == undefined) {
        appUser.value.homePageLayout = {
          horizontal: 0,
          vertical: 0,
        };
      }

      appUser.value.homePageLayout.vertical = vertical;
      await saveUser();
    };

    const homePageVerticalLayout = computed(() => {
      return appUser.value?.homePageLayout?.vertical ?? 50;
    });

    const homePageHorizontalLayout = computed(() => {
      return appUser.value?.homePageLayout?.horizontal ?? 25;
    });

    watch(adminOrganizationIds, () => {
      streamOrganizationConfigs();
    });

    watch(linkedOrganizationIds, () => {
      streamOrganizationConfigs();
    });

    const streamOrganizationConfigs = () => {
      const organizationIds = [
        ...new Set([
          ...adminOrganizationIds.value,
          ...linkedOrganizationIds.value,
        ]),
      ];

      const allStreams = organizationIds.map((id) => {
        return OrganizationsService.streamOrganizationLMSConfig(id);
      });

      const subscription = combineLatest(allStreams).subscribe((data) => {
        lmsOrganizationConfig.value = data.filter(
          (d) => d != undefined
        ) as LMSOrganizationConfig[];
      });

      lmsOrganizationConfigSubscription.value = subscription;
    };

    const organiationLMSConfig = computed(() => {
      return (organizationId: string) => {
        return lmsOrganizationConfig.value.find(
          (config) => config.id == organizationId
        );
      };
    });

    const organizationHasIEWLMS = computed(() => {
      return (organizationId: string) => {
        return organiationLMSConfig.value(organizationId)?.provider == "iew";
      };
    });

    const hideStudentTutorialBanner = computed(() => {
      return appUser.value?.hideStudentTutorialBanner ?? false;
    });

    // Fitlers
    const showFilters = ref(false);
    const levelFilter = ref<string | undefined>(undefined);
    const lessonFilter = ref<string | undefined>(undefined);
    const searchQuery = ref<string>("");
    const sortKey = ref<
      | "lastUpdatedTimestamp"
      | "submittedAtTimestamp"
      | "createOnTimestamp"
      | undefined
    >(appUser.value?.preferredDocumentSortField ?? "submittedAtTimestamp");
    const sortDirection = ref<"desc" | "asc">(
      appUser.value?.preferredDocumentSortOrder ?? "desc"
    );

    return {
      appUser,
      actorClaims,
      lmsUserConfig,
      commentColor,
      hasLMSConfig,
      hasIEWLMS,
      hasSakaiLMS,
      hasClassroomMode,
      isUserAdmin,
      isUserSystemAdmin,
      isOnlineInstructor,
      isIEWInstructor,
      isUserProductReviewer,
      isDemoAccount,
      isPublicDemo,
      demoMode,
      isAdvancedClassroomSettingsEnabled,
      availableIEWProductCategoryIds,
      availableLevelIds,
      availableLessonIds,
      classroomPermissions,
      studentPermissions,
      studentAccountTypes,
      studentAccountType,
      isStudentAccount,
      hasStudentAccount,
      lastViewedTipIndex,
      lastViewedTipDate,
      hideTips,
      hasLinkedGoogleAccount,
      adminOrganizationIds,
      isOrganizationAdministrator,
      isAdministatorForMultipleOrganizations,
      isAdminOfOrganization,
      isPartOfAnyOrganization,
      linkedOrganizationIds,
      initializeAppUserData,
      initializeUserClaims,
      loadUserPermissions,
      updateUserLastViewedTip,
      saveUser,
      logout,

      markTourAsComplete,
      isTourComplete,

      hideTutorials,
      shoudHideTutorial,

      numGradedDocuments,

      hideSubmissionNotice,
      shouldHideSubmissionNotice,

      hasZoomOauthToken,

      hasGoogleOauthToken,
      hasGoogleScope,

      isInitialized,
      isUserClaimsInitialized,
      isLoggedIn,
      isDBClaimsInitialized,
      createdOnTimestamp,

      // Permisison chekcs
      hasPermission,
      canWriteUserRoles,
      canWriteUserPermissions,
      hasInitializedUserLMSConfig,

      showOtherUsersClassrooms,
      showOtherUsersSubmissions,

      updateHomePageHorizontalLayout,
      updateHomePageVerticalLayout,
      homePageVerticalLayout,
      homePageHorizontalLayout,

      organiationLMSConfig,
      organizationHasIEWLMS,

      hideStudentTutorialBanner,

      // Filters
      showFilters,
      levelFilter,
      lessonFilter,
      searchQuery,
      sortKey,
      sortDirection,
    };
  });
